import { z } from 'zod';
import { VALIDATION_CONSTANTS, MIN_PASSWORD_LENGTH, MAX_PAGE_SIZE, CART_CONSTANTS, CHECKOUT_CONSTANTS, } from './constants';
// Product validation
export const ProductSchema = z.object({
    id: z.string(),
    bsaleId: z.number(),
    name: z.string().min(1),
    description: z.string().nullable(),
    category: z.string(),
    brand: z.string().nullable(),
    imageUrl: z.string().url().nullable(),
    price: z.number().positive(),
    originalPrice: z.number().positive().nullable(),
    discountPercent: z.number().min(0).max(CART_CONSTANTS.PROGRESS_PERCENTAGE_FULL).nullable(),
    state: z.number(),
    stockControl: z.boolean(),
});
export const ProductVariantSchema = z.object({
    id: z.string(),
    bsaleId: z.number(),
    productId: z.string(),
    description: z.string(),
    sku: z.string().nullable(),
    barcode: z.string().nullable(),
    price: z.number().positive(),
    stock: z.number().min(0),
    stockAvailable: z.number().min(0),
    state: z.number(),
});
// Cart validation
export const CartItemSchema = z.object({
    variantId: z.string(),
    productId: z.string(),
    name: z.string(),
    imageUrl: z.string().nullable(),
    sku: z.string().nullable(),
    price: z.number().positive(),
    quantity: z.number().int().positive(),
    maxStock: z.number().int().min(0),
}).refine((data) => data.quantity <= data.maxStock, {
    message: 'Quantity cannot exceed available stock',
    path: ['quantity'],
});
export const AddToCartSchema = z.object({
    variantId: z.string(),
    quantity: z.number().int().positive().default(1),
});
export const UpdateCartItemSchema = z.object({
    quantity: z.number().int().positive(),
});
// Customer validation
export const CustomerSchema = z.object({
    firstName: z.string().min(1).max(VALIDATION_CONSTANTS.MAX_NAME_LENGTH),
    lastName: z.string().min(1).max(VALIDATION_CONSTANTS.MAX_NAME_LENGTH),
    email: z.string().email(),
    phone: z.string().regex(/^\+?[0-9]{9,15}$/).nullable().optional(),
    documentType: z.enum(['dni', 'ruc', 'ce']).nullable().optional(),
    documentNumber: z.string().nullable().optional(),
    company: z.string().nullable().optional(),
});
export const CreateCustomerSchema = CustomerSchema;
export const UpdateCustomerSchema = CustomerSchema.partial();
export const CustomerAddressSchema = z.object({
    label: z.string().nullable().optional(),
    address: z.string().min(1).max(VALIDATION_CONSTANTS.MAX_ADDRESS_LENGTH),
    city: z.string().min(1).max(VALIDATION_CONSTANTS.MAX_NAME_LENGTH),
    district: z.string().min(1).max(VALIDATION_CONSTANTS.MAX_NAME_LENGTH),
    zipCode: z.string().nullable().optional(),
    reference: z.string().nullable().optional(),
    isDefault: z.boolean().default(false),
});
// Checkout validation
export const CheckoutSchema = z.object({
    shippingAddressId: z.string(),
    shippingTypeId: z.string(),
    paymentTypeId: z.string(),
    note: z.string().max(VALIDATION_CONSTANTS.MAX_DESCRIPTION_LENGTH).nullable().optional(),
});
export const GuestCheckoutSchema = z.object({
    customer: CustomerSchema,
    address: CustomerAddressSchema,
    shippingTypeId: z.string(),
    paymentTypeId: z.string(),
    note: z.string().max(VALIDATION_CONSTANTS.MAX_DESCRIPTION_LENGTH).nullable().optional(),
});
// Search validation
export const SearchQuerySchema = z.object({
    q: z.string().min(1).max(VALIDATION_CONSTANTS.MAX_EMAIL_LENGTH),
    category: z.string().optional(),
    brand: z.string().optional(),
    minPrice: z.number().positive().optional(),
    maxPrice: z.number().positive().optional(),
    inStock: z.boolean().optional(),
    sortBy: z.enum(['relevance', 'price_asc', 'price_desc', 'newest']).optional(),
    limit: z.number().int().min(1).max(MAX_PAGE_SIZE).default(VALIDATION_CONSTANTS.PRODUCTS_PER_PAGE_DEFAULT),
    offset: z.number().int().min(0).default(0),
});
// AI validation
export const ChatMessageSchema = z.object({
    sessionId: z.string().uuid(),
    message: z.string().min(1).max(VALIDATION_CONSTANTS.MAX_MESSAGE_LENGTH),
    customerId: z.string().optional(),
});
export const PetProfileSchema = z.object({
    type: z.enum(['dog', 'cat', 'bird', 'fish', 'rodent', 'reptile', 'other']),
    breed: z.string().optional(),
    name: z.string().optional(),
    age: z.string().optional(),
    weight: z.number().positive().optional(),
    problems: z.array(z.string()).optional(),
    allergies: z.array(z.string()).optional(),
});
export const RecommendationRequestSchema = z.object({
    problem: z.string().optional(),
    petProfile: PetProfileSchema.optional(),
    category: z.string().optional(),
    cartItems: z.array(z.string()).optional(),
    customerId: z.string().optional(),
    limit: z.number().int().min(1).max(VALIDATION_CONSTANTS.MAX_PHONE_LENGTH).default(CHECKOUT_CONSTANTS.DEFAULT_MAX_CART_QUANTITY),
});
// Webhook validation
export const WebhookEventSchema = z.object({
    topic: z.string(),
    resourceId: z.number(),
    timestamp: z.string(),
    data: z.object({
        href: z.string(),
    }),
});
// Pagination validation
export const PaginationSchema = z.object({
    limit: z.number().int().min(1).max(MAX_PAGE_SIZE).default(VALIDATION_CONSTANTS.PAGE_SIZE_SMALL),
    offset: z.number().int().min(0).default(0),
});
// Auth validation
export const emailSchema = z.string().email('Invalid email address');
export const passwordSchema = z
    .string()
    .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/\d/, 'Password must contain at least one number')
    .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, 'Password must contain at least one special character');
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Password is required'),
});
export const registerSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    firstName: z.string().min(1, 'First name is required').max(VALIDATION_CONSTANTS.MAX_NAME_LENGTH),
    lastName: z.string().min(1, 'Last name is required').max(VALIDATION_CONSTANTS.MAX_NAME_LENGTH),
});
export const refreshTokenSchema = z.object({
    refreshToken: z.string().min(1, 'Refresh token is required'),
});
export const verifyEmailSchema = z.object({
    token: z.string().min(1, 'Verification token is required'),
});
export const resetPasswordRequestSchema = z.object({
    email: emailSchema,
});
export const resetPasswordSchema = z.object({
    token: z.string().min(1, 'Reset token is required'),
    newPassword: passwordSchema,
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
});
export const updateCustomerProfileSchema = z.object({
    firstName: z.string().min(1).max(VALIDATION_CONSTANTS.MAX_NAME_LENGTH).optional(),
    lastName: z.string().min(1).max(VALIDATION_CONSTANTS.MAX_NAME_LENGTH).optional(),
    phone: z.string().regex(/^\+?[0-9]{9,15}$/).nullable().optional(),
    documentType: z.enum(['dni', 'ruc', 'ce']).nullable().optional(),
    documentNumber: z.string().nullable().optional(),
});
export const googleAuthSchema = z.object({
    idToken: z.string().min(1, 'Google ID token is required'),
});
/**
 * Format Zod validation errors into a user-friendly message
 */
export function formatValidationError(error) {
    return error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(', ');
}
// ==================== Admin Validation ====================
export const adminLoginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Password is required'),
});
export const createAdminSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    name: z.string().min(1, 'Name is required').max(VALIDATION_CONSTANTS.MAX_NAME_LENGTH),
    role: z.enum(['superadmin', 'admin']).default('admin'),
});
export const updateAdminSchema = z.object({
    email: emailSchema.optional(),
    name: z.string().min(1).max(VALIDATION_CONSTANTS.MAX_NAME_LENGTH).optional(),
    role: z.enum(['superadmin', 'admin']).optional(),
    isActive: z.boolean().optional(),
});
export const updateAdminPasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
});
// Settings validation
export const settingCategorySchema = z.enum(['analytics', 'seo', 'checkout', 'features', 'hero', 'ai']);
export const updateSettingSchema = z.object({
    value: z.string(), // JSON stringified value
});
export const updateSettingsBatchSchema = z.object({
    settings: z.array(z.object({
        key: z.string(),
        value: z.string(),
    })),
});
// Hero slide validation
export const createHeroSlideSchema = z.object({
    imageUrl: z.string().url('Invalid image URL'),
    imageUrlMobile: z.string().url('Invalid mobile image URL').nullable().optional(),
    title: z.string().max(VALIDATION_CONSTANTS.MAX_EMAIL_LENGTH).nullable().optional(),
    subtitle: z.string().max(VALIDATION_CONSTANTS.MAX_DESCRIPTION_LENGTH).nullable().optional(),
    linkUrl: z.string().url('Invalid link URL').nullable().optional(),
    linkText: z.string().max(VALIDATION_CONSTANTS.PAGE_SIZE_MEDIUM).nullable().optional(),
    backgroundColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format').nullable().optional(),
    textColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format').default('#ffffff'),
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().default(true),
    startsAt: z.string().datetime().nullable().optional(),
    endsAt: z.string().datetime().nullable().optional(),
});
export const updateHeroSlideSchema = createHeroSlideSchema.partial();
export const reorderHeroSlidesSchema = z.object({
    orderedIds: z.array(z.string().uuid()),
});
